import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Collapse, IconButton, Switch, Tooltip, Typography,
} from '@material-ui/core';
import {
  CategoryOutlined,
  ExpandMore,
  FilterListOutlined,
  HistoryOutlined,
  LabelOutlined,
  MoreHoriz,
  SearchOutlined,
  SortOutlined,
  SpellcheckOutlined,
  TuneOutlined,
  ViewModuleOutlined,
  WidgetsOutlined,
} from '@material-ui/icons';
import { getDescriptor } from '../../../../search-experience-v2/core/registry/ComponentRegistry';
import CanvasResolver from '../../../../search-experience-v2/core/renderer/CanvasResolver';
import DynamicPropertiesForm, {
  isRenderableProperty,
} from '../../../../search-experience-v2/editor/properties/DynamicPropertiesForm';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import {
  getEditorComponentDescription,
  getEditorComponentTitle,
} from './editorLocalization';
import styles from './ComponentSettingsPanel.module.scss';

const CONFIGURABLE_TYPES = [
  'products',
  'facets',
  'suggestions',
  'catSuggestions',
  'history',
  'pills',
  'sort',
  'filtersButton',
  'popularQueries',
  'didYouMean',
  'pagination',
];

const COMPONENT_ICONS = {
  products: ViewModuleOutlined,
  facets: TuneOutlined,
  suggestions: SearchOutlined,
  catSuggestions: CategoryOutlined,
  history: HistoryOutlined,
  pills: LabelOutlined,
  sort: SortOutlined,
  filtersButton: FilterListOutlined,
  didYouMean: SpellcheckOutlined,
  pagination: MoreHoriz,
};

/**
 * Traverse config tree to find configurable components (Recursive)
 * @param {Array} nodes - List of component nodes
 * @param {Array} types - keys to look for
 * @param {Array} result - accumulator
 */
const findComponents = (nodes, types, result = []) => {
  if (!nodes || !Array.isArray(nodes)) return result;

  nodes.forEach((node) => {
    if (types.includes(node.type)) {
      result.push(node);
    }
    // Recursively check children
    if (node.nodes) {
      findComponents(node.nodes, types, result);
    }
  });

  return result;
};

// Helper to find node by ID recursively
const findNodeById = (nodes, id) => {
  if (!nodes || !Array.isArray(nodes)) return null;

  let result = null;
  nodes.some((node) => {
    if (node.id === id) {
      result = node;
      return true;
    }
    if (node.nodes) {
      const found = findNodeById(node.nodes, id);
      if (found) {
        result = found;
        return true;
      }
    }
    return false;
  });
  return result;
};

// Deep Clone helper
const clone = (obj) => JSON.parse(JSON.stringify(obj));

const ComponentSettingsPanel = ({ config, view = 'searchBox', screen = 'desktop', device = 'desktop', presentation, onChange }) => {
  const t = useLocalizedMessage();
  const [expandedComponentType, setExpandedComponentType] = useState(undefined);
  const resolutionContext = useMemo(() => ({
    view,
    screen,
    device,
    visualSearch: false, // Force text search mode
    presentation,
  }), [view, screen, device, presentation]);
  // Read and edit the same resolved owner, including measured surface variants.
  const canvas = useMemo(() => CanvasResolver.resolve(config, resolutionContext), [config, resolutionContext]);

  // 2. Find Configurable Components in the flat list
  const components = useMemo(() => {
    if (!canvas || !canvas.nodes) return [];
    const found = findComponents(canvas.nodes, CONFIGURABLE_TYPES);

    // Sort components according to CONFIGURABLE_TYPES order
    return found.sort((a, b) => {
      const indexA = CONFIGURABLE_TYPES.indexOf(a.type);
      const indexB = CONFIGURABLE_TYPES.indexOf(b.type);
      return indexA - indexB;
    });
  }, [canvas]);

  useEffect(() => {
    if (components.length === 0) {
      setExpandedComponentType(undefined);
      return;
    }

    // `null` means the user deliberately collapsed every section. Keep that
    // state stable instead of immediately reopening the first component.
    if (expandedComponentType !== null && !components.some((node) => node.type === expandedComponentType)) {
      const firstVisible = components.find((node) => !node.props?.hidden);
      setExpandedComponentType((firstVisible || components[0]).type);
    }
  }, [components, expandedComponentType]);

  if (!canvas) {
    return (
      <Box className={styles.emptyState}>
        <Typography variant="body2" color="textSecondary">
          <IntlMessages id="ui.embedded.editors.widgetEditor.componentSettingsPanel.noLayoutConfigurationFoundFor" />
          {' '}
          {view}
          /
          {screen}
          {' '}
          (
          {device}
          )
        </Typography>
      </Box>
    );
  }

  if (components.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <Typography variant="body2" color="textSecondary">
          <IntlMessages id="ui.embedded.editors.widgetEditor.componentSettingsPanel.noConfigurableComponentsFound" />
        </Typography>
      </Box>
    );
  }

  const handleToggle = (nodeId, checked) => {
    if (!canvas) return;

    const newConfig = clone(config);
    const newCanvas = CanvasResolver.resolve(newConfig, resolutionContext);
    if (!newCanvas) return;
    const targetNode = findNodeById(newCanvas.nodes, nodeId);

    if (targetNode) {
      if (!targetNode.props) targetNode.props = {};
      targetNode.props.hidden = !checked;
    }

    onChange(newConfig);
  };

  const handlePropsChange = (nodeId, field, value) => {
    if (!canvas) return;

    const newConfig = clone(config);
    const newCanvas = CanvasResolver.resolve(newConfig, resolutionContext);
    if (!newCanvas) return;
    const targetNode = findNodeById(newCanvas.nodes, nodeId);

    if (targetNode) {
      if (!targetNode.props) targetNode.props = {};
      targetNode.props[field] = value;
    }

    onChange(newConfig);
  };

  return (
    <Box className={styles.root}>
      <Typography className={styles.intro}>
        <IntlMessages id="search.editor.settings.intro" />
      </Typography>
      {components.map((node) => {
        const descriptor = getDescriptor(node.type);
        const displayName = getEditorComponentTitle(t, node.type, descriptor?.displayName || node.type);
        const componentDescription = getEditorComponentDescription(t, node.type, descriptor?.description || '');
        const isHidden = node.props?.hidden || false;
        const editableProperties = Object.entries(descriptor?.properties || {})
          .filter(([key]) => key !== 'hidden')
          .filter(([key, value]) => isRenderableProperty(value, node.props?.[key]))
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
        const hasEditableProperties = Object.keys(editableProperties).length > 0;
        const isExpanded = expandedComponentType === node.type && !isHidden && hasEditableProperties;
        const ComponentIcon = COMPONENT_ICONS[node.type] || WidgetsOutlined;
        const sectionClassName = [
          styles.section,
          isExpanded ? styles.sectionExpanded : '',
          isHidden ? styles.sectionHidden : '',
        ].filter(Boolean).join(' ');

        return (
          <Box key={node.type} className={sectionClassName}>
            <Box className={styles.sectionHeader}>
              <Box
                component={hasEditableProperties && !isHidden ? 'button' : 'div'}
                type={hasEditableProperties && !isHidden ? 'button' : undefined}
                className={styles.sectionButton}
                onClick={hasEditableProperties && !isHidden
                  ? () => setExpandedComponentType(isExpanded ? null : node.type)
                  : undefined}
                aria-expanded={hasEditableProperties && !isHidden ? isExpanded : undefined}
              >
                <Box className={styles.iconBox}>
                  <ComponentIcon />
                </Box>
                <Box className={styles.sectionCopy}>
                  <Typography className={styles.sectionTitle}>{displayName}</Typography>
                  {componentDescription && (
                    <Typography className={styles.sectionDescription}>{componentDescription}</Typography>
                  )}
                </Box>
              </Box>
              <Box className={styles.headerActions}>
                <Tooltip title={t(!isHidden ? 'common.shown' : 'common.hidden')}>
                  <Switch
                    className={styles.visibilitySwitch}
                    checked={!isHidden}
                    onChange={(e) => handleToggle(node.id, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    color="primary"
                    size="small"
                    inputProps={{ 'aria-label': `${displayName}: ${t(!isHidden ? 'common.shown' : 'common.hidden')}` }}
                  />
                </Tooltip>
                {hasEditableProperties && !isHidden && (
                  <IconButton
                    size="small"
                    className={`${styles.expandIcon} ${isExpanded ? styles.expandIconOpen : ''}`}
                    onClick={() => setExpandedComponentType(isExpanded ? null : node.type)}
                    aria-label={t(isExpanded ? 'search.editor.section.collapse' : 'search.editor.section.expand')}
                  >
                    <ExpandMore />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Collapse in={isExpanded} timeout={180} unmountOnExit>
              <Box className={styles.properties}>
                <DynamicPropertiesForm
                  componentType={node.type}
                  properties={editableProperties}
                  values={node.props || {}}
                  onChange={(field, value) => handlePropsChange(node.id, field, value)}
                />
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

ComponentSettingsPanel.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  config: PropTypes.object.isRequired,
  view: PropTypes.string,
  screen: PropTypes.string,
  device: PropTypes.string,
  presentation: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

ComponentSettingsPanel.defaultProps = {
  view: 'searchBox',
  screen: 'results',
  device: 'desktop',
  presentation: undefined,
};

export default ComponentSettingsPanel;
